import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChartLineUp, Coins, DownloadSimple, Headset, Lightning, ShieldCheck, UsersThree } from "@phosphor-icons/react";
import useSEO from "@/hooks/useSEO";
import useLatestApk from "@/hooks/useLatestApk";
import { WalletMock } from "@/components/WalletMock";
import { DownloadCard } from "@/components/DownloadCard";
import { FAQ } from "@/components/FAQ";
import { Reveal } from "@/components/Reveal";
import { HERO_STATS } from "@/lib/site";
import useT from "@/i18n";
import { faqLd, softwareLd, webPageLd } from "@/lib/seo";

const FEATURE_ICONS = [Coins, UsersThree, ChartLineUp, ShieldCheck, Lightning, Headset];

const Hero = () => {
  const { apk } = useLatestApk();
  const { t: T, lp } = useT();
  const t = T.home;
  return (
    <section className="pt-12 sm:pt-16 pb-20 relative" aria-labelledby="hero-heading">
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" aria-hidden="true" />
      <div className="max-w-7xl mx-auto safe-x sm:px-8 grid lg:grid-cols-12 gap-12 items-center relative">
        <motion.div className="lg:col-span-7 min-w-0" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="badge-pill mb-6"><Lightning size={12} weight="fill" aria-hidden="true" /> {t.badge}</span>
          <h1 id="hero-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
            {t.h1a}<br />
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(120deg,#00BFFF 0%,#7DDCFF 60%,#fff 100%)" }}>{t.h1b}</span>
          </h1>
          <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
            {t.introA}
            <Link to={lp("/download")} className="text-[#7DDCFF] font-semibold hover:underline">{t.introLink}</Link>{t.introB}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to={lp("/download")} className="btn-primary" data-testid="hero-download-btn">
              <DownloadSimple size={20} weight="bold" aria-hidden="true" />
              {apk ? t.dlV(apk.version_name) : t.dlApk}
            </Link>
            <a href="#features" className="btn-ghost" data-testid="hero-features-btn">{t.explore} <ArrowRight size={16} aria-hidden="true" /></a>
          </div>
          <dl className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
            {HERO_STATS.map((s, i) => (
              <div key={s.label} className="min-w-0">
                <dt className="sr-only">{t.stats[i]}</dt>
                <dd className="font-display text-2xl sm:text-3xl font-bold">{s.value}</dd>
                <dd className="text-xs text-slate-500 tracking-wider uppercase mt-1" aria-hidden="true">{t.stats[i]}</dd>
              </div>
            ))}
          </dl>
        </motion.div>
        <motion.div className="lg:col-span-5 min-w-0" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
          <WalletMock />
        </motion.div>
      </div>
    </section>
  );
};

const Marquee = ({ items }) => (
  <div className="relative overflow-hidden py-6 border-y" style={{ borderColor: "rgba(255,255,255,.06)" }} aria-hidden="true">
    <div className="flex w-max gap-10 whitespace-nowrap text-slate-400 text-sm animate-marquee">
      {[...items, ...items, ...items, ...items].map((m, i) => (
        <span key={i} className="flex items-center gap-3"><span className="w-1 h-1 bg-[#00BFFF] rounded-full inline-block" />{m}</span>
      ))}
    </div>
  </div>
);

const Features = ({ t }) => (
  <section id="features" className="py-24 relative scroll-mt-20" aria-labelledby="features-heading">
    <div className="max-w-7xl mx-auto safe-x sm:px-8">
      <Reveal className="mb-14 max-w-2xl">
        <span className="badge-pill mb-5">{t.featBadge}</span>
        <h2 id="features-heading" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t.featTitle}</h2>
        <p className="mt-4 text-slate-400">{t.featIntro}</p>
      </Reveal>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {t.features.map(([title, text], i) => { const Icon = FEATURE_ICONS[i]; return (
          <Reveal key={title} delay={i * 0.06}>
            <article className="glass rounded-2xl p-7 relative overflow-hidden group h-full transition-colors duration-300 hover:border-[rgba(0,191,255,0.3)]" data-testid={`feature-card-${i}`}>
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(circle, rgba(0,191,255,.25) 0%, transparent 60%)" }} aria-hidden="true" />
              <Icon size={28} weight="duotone" className="text-[#00BFFF]" aria-hidden="true" />
              <h3 className="font-display text-lg font-semibold mt-5">{title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{text}</p>
            </article>
          </Reveal>
        ); })}
      </div>
    </div>
  </section>
);

const DownloadSection = ({ t, lp }) => (
  <section id="download" className="py-24 relative scroll-mt-20" aria-labelledby="download-heading">
    <div className="max-w-7xl mx-auto safe-x sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
      <Reveal className="lg:col-span-5">
        <span className="badge-pill mb-5">{t.dlBadge}</span>
        <h2 id="download-heading" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t.dlTitleA}<br className="hidden sm:block" /> {t.dlTitleB}</h2>
        <p className="mt-4 text-slate-400 leading-relaxed">
          {t.dlIntroA}
          <Link to={lp("/latest-version")} className="text-[#7DDCFF] hover:underline">{t.dlIntroLink}</Link>{t.dlIntroB}
        </p>
        <Link to={lp("/download")} className="btn-primary mt-6" data-testid="hero-goto-download"><DownloadSimple size={18} weight="bold" aria-hidden="true" /> {t.goto}</Link>
      </Reveal>
      <Reveal className="lg:col-span-7 min-w-0" delay={0.1}><DownloadCard /></Reveal>
    </div>
  </section>
);

const AboutTeaser = ({ t, lp }) => (
  <section className="py-24" aria-labelledby="about-teaser">
    <div className="max-w-7xl mx-auto safe-x sm:px-8 grid lg:grid-cols-2 gap-12">
      <Reveal>
        <span className="badge-pill mb-5">{t.aboutBadge}</span>
        <h2 id="about-teaser" className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t.aboutTitle}</h2>
        <p className="mt-6 text-slate-400 leading-relaxed">{t.aboutText}</p>
        <Link to={lp("/about")} className="btn-ghost mt-6" data-testid="learn-more-about">{t.learnMore} <ArrowRight size={16} aria-hidden="true" /></Link>
      </Reveal>
      <div className="space-y-4">
        {t.pillars.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.08}>
            <div className="glass rounded-2xl p-6"><h3 className="font-display font-semibold text-lg">{t}</h3><p className="text-sm text-slate-400 mt-2 leading-relaxed">{d}</p></div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  const { apk } = useLatestApk();
  const { t: T, lp } = useT();
  const t = T.home;
  const title = t.seoTitle;
  const description = t.seoDesc;
  useSEO({ title, description, path: "/", jsonLd: [webPageLd({ title, description, path: lp("/") }), softwareLd(apk, lp), faqLd(t.faq)] });

  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee items={t.marquee} />
      <Features t={t} />
      <DownloadSection t={t} lp={lp} />
      <AboutTeaser t={t} lp={lp} />
      <section className="py-20" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto safe-x sm:px-8">
          <span className="badge-pill mb-5">{t.faqBadge}</span>
          <h2 id="faq-heading" className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t.faqTitle}</h2>
          <p className="mt-3 text-slate-400">{t.faqIntro}</p>
          <div className="mt-10"><FAQ key={T.htmlLang} items={t.faq} /></div>
        </div>
      </section>
    </div>
  );
}
